import { useEffect, useState } from 'react';
import { CouponModel } from '../../../models/coupon';
import axios from '../../../lib/axios';
import { DELETE_COUPON, FIND_MANY_COUPON } from '../../../lib/endpoints/coupon';
import { CouponEnum } from '../../../enums/couponEnum';
import { MerChantModel } from '../../../models/merChantModel';
import { FIND_MANY_MERCHANT_STORE } from '../../../lib/endpoints/merchantUser';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { SelectChangeEvent } from '@mui/material';

export const useCouponController = () => {
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [loadingMainData, setLoadingMainData] = useState<boolean>(true);
  const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);
  const [couponType, setCouponType] = useState<CouponEnum>(CouponEnum.AMOUNT);

  const [data, setData] = useState<CouponModel[]>([]);
  const [branchData, setBranchData] = useState<MerChantModel[]>([]);
  const [branchValue, setBranchValue] = useState<string>('All');

  const [selectedId, setSelectedId] = useState<string>(null!);
  const [selectedCoupon, setSelectedCoupon] = useState<CouponModel>(null!);

  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openDeleteForm, setOpenDeleteForm] = useState<boolean>(false);

  const assignMerchantToAdmin = useSelector((state: RootState) => state?.auth?.data?.merchants);
  const adminRole = useSelector((state: RootState) => state.auth?.data?.role?.role_type);

  const handleSelectedBranch = (event: SelectChangeEvent) => {
    setBranchValue(event.target.value);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setCurrentPage(newPage);
  };

  const handleOpenEditCoupon = () => {
    setOpenForm(true);
    const getData = filterData.filter((data) => data.id === selectedId)[0];
    setSelectedCoupon(getData);
  };

  const handleOpenForm = () => {
    setOpenMenu(null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setOpenMenu(null);
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedId(getId);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleOpenDeleteForm = () => {
    setOpenDeleteForm(true);
  };

  const handleCloseDeleteForm = () => {
    setOpenDeleteForm(false);
  };

  const handleSearchValue = (e: any) => {
    setCurrentPage(1);
    setSearchValue(e.target.value);
  };

  const filterDataByTypeCoupon: CouponModel[] = data?.filter((item: CouponModel) => {
    if (couponType === CouponEnum.AMOUNT) {
      return item.couponType === CouponEnum.AMOUNT;
    } else if (couponType === CouponEnum.PERCENT) {
      return item.couponType === CouponEnum.PERCENT;
    } else if (couponType === CouponEnum.DELIVERY_FREE) {
      return item.couponType === CouponEnum.DELIVERY_FREE;
    } else if (couponType === CouponEnum.DELIVERY_FEE_AMOUNT) {
      return item.couponType === CouponEnum.DELIVERY_FEE_AMOUNT;
    } else if (couponType === CouponEnum.DELIVERY_FEE_PERCENT) {
      return item.couponType === CouponEnum.DELIVERY_FEE_PERCENT;
    }
    return false;
  });

  const filterDataByBranch: CouponModel[] = filterDataByTypeCoupon?.filter((item: CouponModel) => {
    if (branchValue === 'All') {
      return true;
    }
    return item?.merchantId === branchValue;
  });

  const filterData: CouponModel[] = filterDataByBranch.filter((item: CouponModel) =>
    item?.name?.toLowerCase()?.includes(searchValue.toLowerCase())
  );

  const rows: CouponModel[] = filterData?.slice((currentPage - 1) * 10, currentPage * 10);

  const isSearchNotFound = !filterData.length && !!searchValue;
  const countPagination = Math.ceil(filterData?.length / 10);

  const handleFetchData = async () => {
    try {
      setLoadingData(true);
      const resCoupon = await axios.post(FIND_MANY_COUPON);

      setData(resCoupon.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleFetchMainData = async () => {
    try {
      setLoadingMainData(true);
      const branchResponse = await axios.get(FIND_MANY_MERCHANT_STORE);

      if (branchResponse?.data?.length > 0) {
        const matchingMerchants = branchResponse.data.filter((merchant: any) =>
          assignMerchantToAdmin?.includes(merchant?.id)
        );

        if (adminRole === 'SUPER_ADMIN') {
          setBranchData(branchResponse?.data);
        } else if (adminRole === 'ADMIN') {
          if (matchingMerchants.length > 0) {
            setBranchData(matchingMerchants);
          }
        }
      }
    } catch (err) {
    } finally {
      setLoadingMainData(false);
    }
  };

  const handleDeleteCoupon = async () => {
    try {
      setOpenMenu(null);
      setLoadingDeleteButton(true);
      await axios.delete(DELETE_COUPON + selectedId);
      await handleFetchData();
      setOpenDeleteForm(false);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingDeleteButton(false);
    }
  };

  useEffect(() => {
    handleFetchData();
    handleFetchMainData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleChangePage,
    searchValue,
    openForm,
    openMenu,
    handleSearchValue,
    handleCloseForm,
    handleCloseMenu,
    handleOpenForm,
    handleOpenMenu,
    isSearchNotFound,
    currentPage,
    countPagination,
    rows,
    data,
    loadingData,
    handleDeleteCoupon,
    handleFetchData,
    loadingDeleteButton,
    handleOpenDeleteForm,
    handleCloseDeleteForm,
    openDeleteForm,
    handleOpenEditCoupon,
    selectedCoupon,
    setSelectedCoupon: (newValue: CouponModel) => setSelectedCoupon(newValue),
    handleTabCouponPage: (newValue: CouponEnum) => {
      setCouponType(newValue);
      setCurrentPage(1);
    },
    couponType,
    handleSelectedBranch,
    branchValue,
    branchData,
    loadingMainData,
    filterDataByBranch,
  };
};
